<script lang="ts" context="module">
	interface SketchfabApi {
		start(): void;
		gotoAnnotation(index: number): void;
		addEventListener(event: string, callback: () => void): void;
		removeEventListener(event: string, callback: () => void): void;
	}

	interface SketchfabOptions {
		ui_infos?: boolean;
		ui_watermark?: boolean;
		autostart?: boolean;
		success?(api: SketchfabApi): void;
	}

	declare class Sketchfab {
		constructor(version: string, iframe: HTMLIFrameElement);

		public init(uid: string, options: SketchfabOptions): void;
	}
</script>

<script lang="ts">
	import translations from "./InteractiveBrowser.translations.json";
	import { getTranslate } from "../utils/getTranslate.js";
	import type { ProductOption } from "./hp/ProductOption.js";
	import Button from "./Button.svelte";
	import type { LocalizedString } from "../../../core/schema/Locale.js";
	import { getTenant } from "../contexts/tenantContextKey.js";
	import type { ActionReturn } from "svelte/action";
	import ProductButtonGroup from "./hp/ProductButtonGroup.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";

	const translate = getTranslate(translations);
	const { locale } = getTenant();

	let active: ProductOption = "car";

	const sketchfabId: Record<ProductOption, string> = {
		car: "4d5bb2233ffe4e47b26208a81fdfac14",
		truck: "9aa0f734660a4c669a2680613ce4eefa",
	};

	const buttons: Record<ProductOption, { index: number; text: LocalizedString }[]> = {
		car: [
			{ index: 0, text: { cs: "Systémy 5. dveří", en: "Tailgate closures" } },
			{ index: 1, text: { cs: "Kapotové systémy", en: "Bonnet systems" } },
			{ index: 2, text: { cs: "Dveřní systémy", en: "Side door systems" } },
			{ index: 3, text: { cs: "Sedačkové systémy", en: "Seating systems" } },
			{ index: 4, text: { cs: "Dobíjecí a palivové moduly", en: "Charging and fuel-filler modules" } },
			{ index: 5, text: { cs: "Pedálové systémy a ruční brzdy", en: "Pedal and hand brake lever systems" } },
			{ index: 6, text: { cs: "Tažná zařízení", en: "Towing devices" } },
			{ index: 7, text: { cs: "Zvedáky", en: "Jacks" } },
		],
		truck: [
			{ index: 0, text: { cs: "Dveřní systémy", en: "Door systems" } },
			{ index: 1, text: { cs: "Tlumiče", en: "Shock absorbers" } },
			{ index: 2, text: { cs: "Nezávislá topení", en: "Independent heating" } },
			{ index: 3, text: { cs: "Houkačky", en: "Horns" } },
			{ index: 4, text: { cs: "Pedálové systémy a ruční brzdy", en: "Pedal units and handbrake levers" } },
		],
	};

	let sketchfabApi: SketchfabApi | undefined;
	let sketchfabApiReady = false;
	function sketchfab(iframe: HTMLIFrameElement, id: string): ActionReturn<string> {
		const client = new Sketchfab("1.12.1", iframe);
		const options: SketchfabOptions = {
			ui_infos: false,
			ui_watermark: false,
			autostart: false,
			success(api) {
				sketchfabApiReady = false;
				sketchfabApi = api;
				sketchfabApi.addEventListener("viewerready", () => {
					sketchfabApiReady = true;
				});
			},
		};
		client.init(id, options);

		return {
			update(id): void {
				sketchfabApi = undefined;
				client.init(id, { ...options, autostart: true });
			},
		};
	}

	/* eslint-disable svelte/no-at-html-tags */
</script>

<svelte:head>
	{@html `<script type="text/javascript" src="https://static.sketchfab.com/api/sketchfab-viewer-1.12.1.js"></script>`}
</svelte:head>

<div class="container flex min-h-screen flex-col gap-2">
	<div class="grid justify-items-center lg:grid-cols-[auto,auto] xl:grid-cols-3">
		<h2 class="w-full text-[2.75rem] font-semibold lg:text-4xl">{translate("heading")}</h2>
		<ProductButtonGroup bind:active />
	</div>

	<div class="flex flex-1 flex-col gap-8">
		<div class="sketchfab-embed-wrapper container aspect-video flex-1 lg:max-h-[50vh] lg:w-8/12">
			<iframe
				use:sketchfab={sketchfabId[active]}
				class="h-full w-full"
				title={translate("iframeTitle")}
				frameborder="0"
				allowfullscreen
				allow="autoplay; fullscreen; xr-spatial-tracking"
			>
			</iframe>
		</div>
		<div class="flex flex-wrap justify-center gap-4">
			{#each buttons[active] as button}
				<Button
					variant="tertiary"
					class="xl:text-2md !h-auto !w-auto text-xs"
					noDefaultIcon
					on:click={() => {
						sketchfabApi?.start();
						function goto() {
							sketchfabApi?.gotoAnnotation(button.index);
							sketchfabApi?.removeEventListener("viewerready", goto);
						}
						if (sketchfabApiReady) {
							goto();
						} else {
							sketchfabApi?.addEventListener("viewerready", goto);
						}
					}}
					disabled={!sketchfabApi}
				>
					{nbspify(button.text[locale])}
				</Button>
			{/each}
		</div>
	</div>
</div>
